import React, { useState, useEffect, useRef } from "react";
import { Form, Button, Label } from "semantic-ui-react";
import { useAuth } from "../../../../hooks";
import { EnvironmentalObjetivesform } from "../../../../api";
import "./EnvironmentalObjetivesForm.scss";
import { useLanguage } from "../../../../contexts";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { initialValues, validationSchema } from "./EnvironmentalObjetivesForm.form";
import { toast } from "react-toastify";
import { sourcesOptions } from "../../../../utils/aspects";
import { frameworksByAspect } from "../../../../utils/frameworksByAspect";
import { energyKPIsCodes } from "../../../../utils/dataAndFormulas/energyForms";
import { waterKPIsCodes } from "../../../../utils/dataAndFormulas/waterForms";
import { wasteKPIsCodes } from "../../../../utils/dataAndFormulas/wasteForms";
import { effluentKPIsCodes } from "../../../../utils/dataAndFormulas/effluentForms";
import { fuelsKPIsCodes } from "../../../../utils/dataAndFormulas/fuelsForms";
import { fetchBaselineYearsByAspect, getBaselineYearForKpi } from "../../../../utils/dataAndFormulas/kpiBaselineYear";
import { avanceDe } from "../../../../utils/dataAndFormulas/objectivesCalendarCalc";
import { renderTemplate, fillTemplate } from "../../../../utils/renderTemplate";
import { getRealEnergyKpiValue } from "../../../../utils/dataAndFormulas/energyKpiRealValue";
import { getRealWaterKpiValue } from "../../../../utils/dataAndFormulas/waterKpiRealValue";
import { getRealWasteKpiValue } from "../../../../utils/dataAndFormulas/wasteKpiRealValue";
import { getRealEffluentKpiValue } from "../../../../utils/dataAndFormulas/effluentKpiRealValue";
import { getRealFuelsKpiValue } from "../../../../utils/dataAndFormulas/fuelsKpiRealValue";

// Mismo mapa que usa ObjectivesMatrix.js para leer el valor real de un KPI en
// un año puntual — acá se reutiliza solo para avisar si el año de línea base
// elegido a mano tiene datos cargados o no.
const REAL_KPI_GETTER_BY_ASPECT = {
  energy: getRealEnergyKpiValue,
  water: getRealWaterKpiValue,
  waste: getRealWasteKpiValue,
  effluent: getRealEffluentKpiValue,
  fuels: getRealFuelsKpiValue,
};

const environmentalObjetivesCtrl = new EnvironmentalObjetivesform();

// Catálogos reales de KPIs (los mismos que usan los dashboards de cada módulo),
// en vez de una lista inventada. El código (ej. "KPIene1") es la fuente de verdad;
// la etiqueta se resuelve con las traducciones ya existentes para esa misma clave.
const KPI_CODES_BY_ASPECT = {
  energy: energyKPIsCodes,
  water: waterKPIsCodes,
  waste: wasteKPIsCodes,
  effluent: effluentKPIsCodes,
  fuels: fuelsKPIsCodes,
};

// Acota los KPIs mostrados según la Fuente elegida (por "key" de sourcesOptions,
// no por el texto visible). "include" filtra qué claves del catálogo aplican;
// "exclude" saca falsos positivos (ej. "hazardous" también matchea "non_hazardous").
const KPI_KEYWORDS_BY_SOURCE = {
  energy: {
    electricidad_industrial: { include: ["electricity_standard_production"] },
    electricidad_domestica: { include: ["electricity_standard_domestic"] },
    energia_electrica: { include: ["electricity_standard", "total_electricity"] },
    energia_renovable: { include: ["renewable_energies"] },
    diesel: { include: ["diesel"] },
    vapor: { include: ["steam"] },
    gas_natural: { include: ["natural_gas"] },
    glp: { include: ["glp"] },
    hfo: { include: ["heavy_fuel_oil"] },
    lfo: { include: ["light_fuel_oil"] },
    carbon: { include: ["coal"] },
    gasolina: { include: ["gasoline"] },
    biomasa: { include: ["biomass"] },
    energia_total: { include: ["total_energy"] },
  },
  fuels: {
    gas_natural: { include: ["_natural_gas"] },
    glp: { include: ["_glp"] },
    diesel: { include: ["_diesel"] },
    gasolina: { include: ["_gasoline"] },
    biodiesel: { include: ["_biodiesel"] },
    etanol: { include: ["_ethanol"] },
    elect_vehiculos: { include: ["_electricity_vehicles"] },
  },
  water: {
    agua_total: { include: ["total_water"] },
    agua_industrial: { include: ["production"] },
    agua_domestica: { include: ["domestic"] },
    agua_municipal: { include: ["municipal_network"] },
    agua_lluvia: { include: ["rainwater"] },
    agua_subterranea: { include: ["groundwater"] },
    agua_superficial: { include: ["surface_water"] },
    agua_condensada: { include: ["steam_condensed_water"] },
  },
  waste: {
    no_peligrosos: { include: ["non_hazardous"] },
    peligrosos: { include: ["hazardous"], exclude: ["non_hazardous"] },
    papel_carton: { include: ["paper_and_cardboard"] },
    plastico: { include: ["plastic"] },
    tela: { include: ["fabric_or_scraps"] },
    metal: { include: ["metal"] },
    madera: { include: ["wood"] },
    otros: { include: ["other_non_hazardous", "other_hazardous"] },
    comida: { include: ["leftover_food"] },
    vidrio: { include: ["glass"] },
    cuero: { include: ["leather"] },
    espuma: { include: ["foam"] },
    electronicos: { include: ["electronic_waste"] },
    latas_no_peligrosas: { include: ["empty_cans_or_containers_not_dangerous"] },
    baterias: { include: ["batteries"] },
    tubos: { include: ["fluorescent_tubes"] },
    aceite_cocina: { include: ["cooking_oil"] },
    aceite_mantenimiento: { include: ["maintenance_oil"] },
    lubricantes: { include: ["lubricants"] },
    pinturas: { include: ["paint_remains"] },
    cenizas: { include: ["coal_ashes"] },
    caucho: { include: ["rubber"] },
  },
  effluent: {
    industrial: { include: ["industrial_effluents"] },
    domestico: { include: ["domestic_effluents"] },
    total: { include: ["total_", "combined_effluents"] },
    lodos: { include: ["sludge_and_mud"] },
  },
};

function matchesKeywords(labelKey, keywords) {
  return (
    keywords.include.some((kw) => labelKey.includes(kw)) &&
    !(keywords.exclude || []).some((kw) => labelKey.includes(kw))
  );
}

// Coherencia KPI → Aspecto/Fuente (dirección inversa a como se filtra el select
// de KPI): el KPI es la fuente de verdad. Dado un código, resuelve a qué aspecto
// pertenece y qué "Fuente" de sourcesOptions corresponde, reusando los mismos
// catálogos/keywords que ya filtran el select de KPI.
function resolveAspectSourceFromKpi(code) {
  for (const [asp, catalog] of Object.entries(KPI_CODES_BY_ASPECT)) {
    const labelKey = Object.keys(catalog).find((k) => catalog[k] === code);
    if (!labelKey) continue;

    const keywordsBySource = KPI_KEYWORDS_BY_SOURCE[asp] || {};
    const sourceKey = Object.keys(keywordsBySource).find((sk) =>
      matchesKeywords(labelKey, keywordsBySource[sk])
    );
    const sourceValue = sourceKey
      ? sourcesOptions[asp]?.find((o) => o.key === sourceKey)?.value
      : null;

    return { aspect: asp, source: sourceValue || null };
  }
  return null;
}

export function EnvironmentalObjetivesForm(props) {
  const { onClose, onReload, objetivesForm, siteSelected, year, aspect, sumImpact, onCreated } = props;

  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const navigate = useNavigate();

  const t = (key) => translations[key] || key;

  const aspectosOptions = [
    { key: "energy",        text: t("energy"),        value: "energy"        },
    { key: "water",         text: t("water"),         value: "water"         },
    { key: "waste",         text: t("waste"),         value: "waste"         },
    { key: "effluent",      text: t("effluent"),      value: "effluent"      },
    { key: "fuels",         text: t("fuels"),         value: "fuels"         },
    { key: "health_safety", text: t("health_safety"), value: "health_safety" },
    { key: "social",        text: t("social"),        value: "social"        },
  ];

  const accionOptions = [
    "Aumentar", "Reducir", "Mantener", "Implementar", "Ejecutar", "Sustituir",
    "Diseñar", "Definir", "Validar", "Monitorear", "Diagnosticar", "Verificar",
    "Optimizar", "Modernizar", "Capacitar", "Sensibilizar",
  ].map((v) => ({ key: v, text: t(v), value: v }));

  const frequencyOptions = ["Mensual", "Trimestral", "Semestral", "Anual"].map((v) => ({
    key: v, text: t(v), value: v,
  }));

  const baseMedicionOptions = [
    { key: "ambas",       text: t("base_medicion_option_ambas"),       value: "ambas" },
    { key: "absoluta",    text: t("base_medicion_option_absoluta"),    value: "absoluta" },
    { key: "normalizada", text: t("base_medicion_option_normalizada"), value: "normalizada" },
  ];

  const startYear = 2020;
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear + 25 - startYear + 1 }, (_, i) => startYear + i);
  const yearsOptions = years.map((y) => ({ key: y, text: y.toString(), value: y }));

  // ── Año de línea base del KPI (se lee de ☰ → Datos → Línea Base, nunca se
  // carga a mano acá). Es la configuración VIGENTE hoy — para un objetivo ya
  // creado, lo que realmente se usa es el valor congelado en
  // objetivesForm.baselineYear (ver displayedBaselineYear), no este valor en
  // vivo, para no correr la línea base bajo objetivos vigentes si alguien
  // cambia la configuración después. Se declara ACÁ (antes de useFormik) para
  // que la validación del año meta pueda compararlo contra el año base real
  // en vez de contra el año calendario actual.
  const [kpiBaselineYear, setKpiBaselineYear] = useState(null);
  const [kpiBaselineLoading, setKpiBaselineLoading] = useState(false);
  // Disponibilidad de datos reales para el año de línea base elegido a mano
  // (ver useEffect más abajo y el nuevo campo "Año de línea base").
  const [baselineAvailability, setBaselineAvailability] = useState({ checking: false, available: null });

  // El objetivo ya guardado manda (congelado al crearlo); si todavía no existe
  // o es viejo y no tiene baselineYear guardado, se muestra la config vigente.
  const displayedBaselineYear = objetivesForm?.baselineYear ?? kpiBaselineYear;

  // Traba sincrónica contra doble-submit (un doble click puede disparar
  // onSubmit dos veces antes de que React repinte formik.isSubmitting/el
  // botón deshabilitado — un ref se actualiza al instante, sin ese retraso).
  const submittingRef = useRef(false);

  const formik = useFormik({
    initialValues: initialValues(objetivesForm, year, aspect),
    validationSchema: validationSchema(sumImpact, t, displayedBaselineYear),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        let created = false;
        // "SINKPI" es solo un sentinel de UI para el select — al backend se
        // manda kpiCode:null, así los `if (!o.kpiCode)` que ya existen río
        // abajo (Matriz, *KpiRealValue.js) siguen funcionando sin cambios.
        if (formValue.kpiCode === "SINKPI") {
          formValue.kpiCode = null;
          // "Sin KPI" no tiene línea base real asociada (aspecto/fuente los
          // carga el usuario a mano) — no corresponde guardar un año acá.
          formValue.baselineYear = null;
        }
        // Año de línea base: ahora es un campo editable (ver "Año de línea
        // base" en el bloque MEDIBLE) — formValue.baselineYear ya trae lo
        // que el usuario eligió (o lo sugerido automáticamente si no tocó
        // nada). Se guarda tal cual, sin volver a pisarlo acá. Resguardo por
        // si quedó vacío (ej. KPI recién elegido y el fetch todavía no
        // resolvió): cae a la config vigente en ese momento.
        if (!formValue.baselineYear && formValue.kpiCode) {
          formValue.baselineYear = kpiBaselineYear;
        }

        // Año meta original: si el objetivo ya estaba vencido (año meta pasado,
        // sin llegar al 100% bottom-up) y ahora se le cambia el año meta (se
        // "extiende" a un año nuevo), queda registro del año original — una
        // sola vez, la primera vez que esto pasa, para no perder cuál fue la
        // fecha con la que realmente se incumplió.
        if (objetivesForm && !objetivesForm.originalYear && formValue.year !== objetivesForm.year) {
          // Avance real (modelo calendario: cumplidas/programadas de sus
          // metas) — antes esto leía m.contribution/m.progress, campos del
          // viejo modelo de puntuación que ya no existen en Meta (spec v6),
          // así que siempre daba 0 y marcaba "vencido" a cualquier objetivo
          // al que se le cambiara el año, sin importar su avance real.
          const wasOverdueAndIncomplete = objetivesForm.year < new Date().getFullYear() && avanceDe(objetivesForm) < 1;
          if (wasOverdueAndIncomplete) {
            formValue.originalYear = objetivesForm.year;
          }
        }

        if (!objetivesForm) {
          formValue.creator_user = user._id;
          formValue.date = new Date();
          if (user?.site) {
            formValue.site = user.site;
          } else if (siteSelected) {
            formValue.site = siteSelected;
          }
          const res = await environmentalObjetivesCtrl.createEnvironmentalObjetivesForm(accessToken, formValue);
          if (res.code === 200) {
            toast.success(t("form_saved_successfully"));
            created = true;
          }
        } else {
          const res = await environmentalObjetivesCtrl.updateEnvironmentalObjetivesForm(accessToken, objetivesForm._id, formValue);
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        }
        setTimeout(() => {
          onReload();
          // La meta M1 se crea sola al guardar (backend); si es un alta y hay
          // onCreated, ESE es el único destino (ej: cambiar a Plan Estratégico)
          // — no navegar aparte, o la navegación gana la carrera y el cambio
          // de pestaña nunca llega a pasar.
          if (created && onCreated) onCreated();
          else if (onClose) onClose();
          else navigate(`/admin/data/data/environmentalobjetives`, { state: { siteForm: siteSelected } });
        }, 1000);
      } catch (error) {
        console.error(error);
      } finally {
        submittingRef.current = false;
      }
    },
  });

  const goBack = () =>
    navigate(`/admin/data/data/environmentalobjetives`, { state: { siteForm: siteSelected } });

  const sourceKey = sourcesOptions[formik.values.aspect]?.find(
    (o) => o.value === formik.values.source
  )?.key;
  const kpiKeywords = KPI_KEYWORDS_BY_SOURCE[formik.values.aspect]?.[sourceKey];

  const kpiEntries = Object.entries(KPI_CODES_BY_ASPECT[formik.values.aspect] || {});
  const kpiEntriesFiltered = kpiKeywords
    ? kpiEntries.filter(([labelKey]) => matchesKeywords(labelKey, kpiKeywords))
    : kpiEntries;
  // Si la Fuente elegida no tiene keywords mapeadas, o el filtro no encuentra
  // nada, mostramos el catálogo completo del aspecto en vez de dejarlo vacío.
  // El KPI es la fuente de verdad (spec v3): si todavía no se eligió un aspecto,
  // el select muestra los catálogos de TODOS los aspectos combinados, para poder
  // elegir el KPI primero y que sincronice Aspecto/Fuente solo.
  // "Sin KPI de Sustemia" — siempre disponible, para cualquier aspecto:
  // objetivos de gestión (Seguridad y Salud, Social) u objetivos de los 5
  // aspectos ambientales sin un KPI de Sustemia puntual detrás. Al elegirla
  // se reemplaza Fuente por un indicador propio en texto libre (ver más abajo).
  const SIN_KPI_OPTION = { key: "SINKPI", value: "SINKPI", text: t("no_kpi_option") };

  const kpiOptions = [
    ...(formik.values.aspect
      ? (kpiEntriesFiltered.length ? kpiEntriesFiltered : kpiEntries).map(
          ([labelKey, code]) => ({ key: code, value: code, text: `${code} · ${t(labelKey)}` })
        )
      : Object.entries(KPI_CODES_BY_ASPECT).flatMap(([, catalog]) =>
          Object.entries(catalog).map(([labelKey, code]) => ({
            key: code, value: code, text: `${code} · ${t(labelKey)}`,
          }))
        )),
    SIN_KPI_OPTION,
  ];
  const isSinKpi = formik.values.kpiCode === "SINKPI";

  // Fetch del año de línea base vigente (ver declaración de kpiBaselineYear
  // más arriba, antes de useFormik). Además sugiere ese año en el campo
  // editable "Año de línea base" — pero solo cuando corresponde autocompletar
  // (objetivo nuevo, sin año propio guardado, o el KPI cambió respecto al
  // guardado): mismo criterio que antes usaba el freeze en onSubmit, para no
  // pisar un año que el usuario ya eligió a mano en este objetivo.
  useEffect(() => {
    const aspect = formik.values.aspect;
    const kpiCode = formik.values.kpiCode;
    if (!aspect || !kpiCode || kpiCode === "SINKPI" || !siteSelected) {
      setKpiBaselineYear(null);
      return;
    }
    let cancelled = false;
    setKpiBaselineLoading(true);
    fetchBaselineYearsByAspect(accessToken, siteSelected, [aspect]).then((formByAspect) => {
      if (cancelled) return;
      const configuredYear = getBaselineYearForKpi(aspect, kpiCode, formByAspect);
      setKpiBaselineYear(configuredYear);
      const shouldSuggest = !objetivesForm || !objetivesForm.baselineYear || objetivesForm.kpiCode !== kpiCode;
      if (shouldSuggest) formik.setFieldValue("baselineYear", configuredYear);
    }).finally(() => {
      if (!cancelled) setKpiBaselineLoading(false);
    });
    return () => { cancelled = true; };
  }, [formik.values.aspect, formik.values.kpiCode, siteSelected, accessToken]);

  // Avisa si el año de línea base elegido a mano tiene datos reales cargados
  // para ese sitio/KPI — si no, se muestra la advertencia pedida por el
  // cliente ("no disponible") en vez de dejarlo pasar en silencio.
  useEffect(() => {
    const aspect = formik.values.aspect;
    const kpiCode = formik.values.kpiCode;
    const year = formik.values.baselineYear;
    const getter = REAL_KPI_GETTER_BY_ASPECT[aspect];
    if (!aspect || !kpiCode || kpiCode === "SINKPI" || !year || !siteSelected || !getter) {
      setBaselineAvailability({ checking: false, available: null });
      return;
    }
    let cancelled = false;
    setBaselineAvailability({ checking: true, available: null });
    getter({ accessToken, site: siteSelected, kpiCode, year })
      .then((res) => {
        if (!cancelled) setBaselineAvailability({ checking: false, available: !!res?.available });
      })
      .catch(() => {
        if (!cancelled) setBaselineAvailability({ checking: false, available: false });
      });
    return () => { cancelled = true; };
  }, [formik.values.aspect, formik.values.kpiCode, formik.values.baselineYear, siteSelected, accessToken]);

  // ── SMART chip progress ──────────────────────────────────────
  const getChipState = (letter) => {
    const v = formik.values;
    switch (letter) {
      case "s": {
        const n = [v.aspect, v.action, v.source, v.measure].filter(Boolean).length;
        return n === 4 ? "is-active" : n > 0 ? "is-partial" : "";
      }
      case "m": {
        const n = [v.reductionTons, v.impact].filter(Boolean).length;
        return n === 2 ? "is-active" : n > 0 ? "is-partial" : "";
      }
      case "a":
        return v.investment ? "is-active" : "";
      case "r":
        return v.relevantConfirmed
          ? "is-active"
          : v.aspect && v.action && v.source
          ? "is-partial"
          : "";
      case "t": {
        const n = [v.year, v.periodStart, v.periodEnd].filter(Boolean).length;
        return n === 3 ? "is-active" : n > 0 ? "is-partial" : "";
      }
      default: return "";
    }
  };

  const badgeTitles = {
    s: t("smart_badge_specific"),
    m: t("smart_badge_measurable"),
    a: t("smart_badge_achievable"),
    r: t("smart_badge_relevant"),
    t: t("smart_badge_timely"),
  };

  // ── R — context helpers ──────────────────────────────────────
  const aspectText  = aspectosOptions.find((o) => o.value === formik.values.aspect)?.text;
  const actionText  = accionOptions.find((o) => o.value === formik.values.action)?.text;
  const showChips   = !!(aspectText && actionText && formik.values.source);

  // ── Preview tokens ───────────────────────────────────────────
  const v = formik.values;
  const previewAction = v.action ? t(v.action) : null;
  const previewYear   = displayedBaselineYear;

  const Token = ({ value, fallback }) =>
    value ? (
      <span className="fill">{value}</span>
    ) : (
      <span className="ph">[{fallback}]</span>
    );

  // Vista previa: "2026-03-15" -> "mar-26" (mes abreviado en español, no numérico).
  // Se parsea el string directamente (sin pasar por Date) para no correr un día
  // por el desfasaje de huso horario que introduce `new Date("YYYY-MM-DD")`.
  const MONTHS_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const formatMonthYearAbbrev = (isoDate) => {
    if (!isoDate) return null;
    const [y, m] = isoDate.split("-");
    return `${MONTHS_ES[Number(m) - 1]}-${y.slice(-2)}`;
  };

  return (
    <Form
      className="smart-form"
      onSubmit={formik.handleSubmit}
      style={{ maxWidth: 1400, margin: "0 auto" }}
    >

      {/* ── Header ── */}
      <div className="smart-form__header">
        <div>
          <h2 className="smart-form__title">
            {objetivesForm ? t("edit_environmentalObjetive_form") : t("new_environmentalObjetive_form")}
          </h2>
          <p className="smart-form__subtitle">
            {t("smart_form_subtitle")}
          </p>
        </div>
        <div className="smart-form__badges" aria-label="Cobertura SMART">
          {["s", "m", "a", "r", "t"].map((l) => (
            <span key={l} className={`smart-badge ${getChipState(l)}`} title={badgeTitles[l]}>
              {l.toUpperCase()}
            </span>
          ))}
        </div>
      </div>

      {/* ── S — ESPECÍFICO ── */}
      <div className="smart-section">
        <div className="smart-section__head">
          <span className="smart-badge sm">S</span>
          <span className="smart-section__title">{t("smart_badge_specific")}</span>
          <span className="smart-section__hint">{t("smart_section_s_hint")}</span>
        </div>

        <div className="smart-row-3 has-bottom">
          {/* Aspecto */}
          <div className="smart-field">
            <Form.Field error={formik.touched.aspect && !!formik.errors.aspect}>
              <label>{t("aspect")} <span className="req">*</span></label>
              <Form.Select
                options={aspectosOptions}
                placeholder={t("select_aspect")}
                value={formik.values.aspect ?? null}
                onChange={(_, { value }) => {
                  formik.setFieldValue("aspect", value);
                  formik.setFieldValue("source", null);
                  formik.setFieldValue("kpiCode", null);
                }}
                onBlur={() => formik.setFieldTouched("aspect", true)}
              />
              <span className="smart-field__hint">{t("smart_hint_aspect")}</span>
              {formik.touched.aspect && formik.errors.aspect && (
                <Label basic color="red" pointing>{formik.errors.aspect}</Label>
              )}
            </Form.Field>
          </div>

          {/* Acción */}
          <div className="smart-field">
            <Form.Field error={formik.touched.action && !!formik.errors.action}>
              <label>{t("action")} <span className="req">*</span></label>
              <Form.Select
                options={accionOptions}
                placeholder={t("select_action")}
                value={formik.values.action ?? null}
                onChange={(_, { value }) => formik.setFieldValue("action", value)}
                onBlur={() => formik.setFieldTouched("action", true)}
              />
              <span className="smart-field__hint">{t("smart_hint_action")}</span>
              {formik.touched.action && formik.errors.action && (
                <Label basic color="red" pointing>{formik.errors.action}</Label>
              )}
            </Form.Field>
          </div>

          {/* Fuente — con "Sin KPI" elegido, se reemplaza por el indicador
              propio en texto libre (mismo campo `source`, sin catálogo). */}
          <div className="smart-field">
            <Form.Field error={formik.touched.source && !!formik.errors.source}>
              <label>{isSinKpi ? t("custom_indicator_label") : t("source")} <span className="req">*</span></label>
              {isSinKpi ? (
                <Form.Input
                  name="source"
                  placeholder={t("custom_indicator_placeholder")}
                  value={formik.values.source ?? ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              ) : (
                <Form.Select
                  options={sourcesOptions[formik.values.aspect] || []}
                  placeholder={t("select_source")}
                  value={formik.values.source ?? null}
                  onChange={(_, { value }) => {
                    formik.setFieldValue("source", value);
                    formik.setFieldValue("kpiCode", null);
                  }}
                  onBlur={() => formik.setFieldTouched("source", true)}
                  disabled={!formik.values.aspect}
                />
              )}
              <span className="smart-field__hint">{isSinKpi ? t("custom_indicator_hint") : t("smart_hint_source")}</span>
              {formik.touched.source && formik.errors.source && (
                <Label basic color="red" pointing>{formik.errors.source}</Label>
              )}
            </Form.Field>
          </div>
        </div>

        {/* Medida — ocupa el ancho completo */}
        <div className="smart-field">
          <Form.Field error={formik.touched.measure && !!formik.errors.measure}>
            <label>{t("measure")} <span className="req">*</span></label>
            <Form.TextArea
              name="measure"
              placeholder={t("smart_placeholder_measure")}
              value={formik.values.measure ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <span className="smart-field__hint">
              {t("smart_hint_measure")}
            </span>
            {formik.touched.measure && formik.errors.measure && (
              <Label basic color="red" pointing>{formik.errors.measure}</Label>
            )}
          </Form.Field>
        </div>
      </div>

      {/* ── M — MEDIBLE ── */}
      <div className="smart-section">
        <div className="smart-section__head">
          <span className="smart-badge sm">M</span>
          <span className="smart-section__title">{t("smart_badge_measurable")}</span>
          <span className="smart-section__hint">{t("smart_section_m_hint")}</span>
        </div>

        <div className="smart-row-4">
          <div className="smart-field">
            <Form.Field error={formik.touched.reductionTons && !!formik.errors.reductionTons}>
              <label>% <span className="req">*</span></label>
              <Form.Input
                type="number" name="reductionTons" min={1} max={100} placeholder="1 - 100"
                value={formik.values.reductionTons ?? ""}
                onChange={formik.handleChange} onBlur={formik.handleBlur}
              />
              <span className="smart-field__hint">{t("smart_hint_reduction_pct")}</span>
              {formik.touched.reductionTons && formik.errors.reductionTons && (
                <Label basic color="red" pointing>{formik.errors.reductionTons}</Label>
              )}
            </Form.Field>
          </div>

          <div className="smart-field">
            <Form.Field error={formik.touched.impact && !!formik.errors.impact}>
              <label>{t("smart_label_impact")} <span className="req">*</span></label>
              <Form.Input
                type="number" step={0.1} min={0.5} max={100} name="impact" placeholder="0.5 - 100"
                value={formik.values.impact ?? ""}
                onChange={formik.handleChange} onBlur={formik.handleBlur}
              />
              <span className="smart-field__hint">{t("smart_hint_impact")}</span>
              {formik.touched.impact && formik.errors.impact && (
                <Label basic color="red" pointing>{formik.errors.impact}</Label>
              )}
            </Form.Field>
          </div>

          <div className="smart-field">
            <Form.Field error={formik.touched.prodGrowth && !!formik.errors.prodGrowth}>
              <label>{t("smart_label_prod_growth")}</label>
              <Form.Input
                type="number" step={0.5} min={0} max={50} name="prodGrowth" placeholder={t("smart_placeholder_example_4")}
                value={formik.values.prodGrowth ?? 4}
                onChange={formik.handleChange} onBlur={formik.handleBlur}
              />
              <span className="smart-field__hint">
                {t("smart_hint_prod_growth")}
              </span>
              {formik.touched.prodGrowth && formik.errors.prodGrowth && (
                <Label basic color="red" pointing>{formik.errors.prodGrowth}</Label>
              )}
            </Form.Field>
          </div>

          <div className="smart-field">
            <Form.Field error={formik.touched.baseMedicion && !!formik.errors.baseMedicion}>
              <label>{t("smart_label_base_medicion")} <span className="req">*</span></label>
              <Form.Select
                options={baseMedicionOptions}
                value={formik.values.baseMedicion ?? "ambas"}
                onChange={(_, { value }) => formik.setFieldValue("baseMedicion", value)}
                onBlur={() => formik.setFieldTouched("baseMedicion", true)}
              />
              <span className="smart-field__hint">
                {t("smart_hint_base_medicion")}
              </span>
              {formik.touched.baseMedicion && formik.errors.baseMedicion && (
                <Label basic color="red" pointing>{formik.errors.baseMedicion}</Label>
              )}
            </Form.Field>
          </div>

          <div className="smart-field">
            <Form.Field error={formik.touched.kpiCode && !!formik.errors.kpiCode}>
              <label>{t("smart_label_kpi")} <span className="req">*</span></label>
              <Form.Select
                options={kpiOptions}
                placeholder={t("select_kpi")}
                value={formik.values.kpiCode ?? null}
                onChange={(_, { value }) => {
                  formik.setFieldValue("kpiCode", value);
                  if (value === "SINKPI") return; // aspecto y fuente/indicador los elige el usuario a mano
                  const resolved = resolveAspectSourceFromKpi(value);
                  if (resolved) {
                    formik.setFieldValue("aspect", resolved.aspect);
                    formik.setFieldValue("source", resolved.source);
                  }
                }}
                onBlur={() => formik.setFieldTouched("kpiCode", true)}
                clearable
              />
              <span className="smart-field__hint">
                {t("smart_hint_kpi")}
              </span>
              {formik.values.kpiCode && (
                <span
                  className={`smart-field__hint smart-field__hint--kpi-value${
                    !kpiBaselineLoading && !displayedBaselineYear ? " smart-field__hint--warning" : ""
                  }`}
                >
                  {kpiBaselineLoading
                    ? t("smart_kpi_baseline_loading")
                    : displayedBaselineYear
                    ? fillTemplate(t("smart_kpi_baseline_value"), { year: displayedBaselineYear })
                    : t("smart_kpi_baseline_unavailable")}
                </span>
              )}
              {formik.touched.kpiCode && formik.errors.kpiCode && (
                <Label basic color="red" pointing>{formik.errors.kpiCode}</Label>
              )}
            </Form.Field>
          </div>

          {/* Año de línea base: editable — el KPI sugiere uno (config vigente
              de ☰ → Datos → Línea Base) pero el usuario puede elegir otro. */}
          {!isSinKpi && (
            <div className="smart-field">
              <Form.Field>
                <label>{t("smart_label_baseline_year")}</label>
                <Form.Select
                  options={yearsOptions}
                  placeholder={t("select")}
                  value={formik.values.baselineYear ?? null}
                  disabled={!formik.values.kpiCode}
                  onChange={(_, { value }) => formik.setFieldValue("baselineYear", value)}
                  clearable
                />
                <span className="smart-field__hint">
                  {t("smart_hint_baseline_year")}
                </span>
                {formik.values.baselineYear && (
                  <span
                    className={`smart-field__hint smart-field__hint--kpi-value${
                      baselineAvailability.available === false ? " smart-field__hint--warning" : ""
                    }`}
                  >
                    {baselineAvailability.checking
                      ? t("smart_baseline_year_checking")
                      : baselineAvailability.available === false
                      ? t("smart_baseline_year_no_data")
                      : baselineAvailability.available === true
                      ? t("smart_baseline_year_ok")
                      : ""}
                  </span>
                )}
              </Form.Field>
            </div>
          )}
        </div>
      </div>

      {/* ── A — ALCANZABLE ── */}
      <div className="smart-section">
        <div className="smart-section__head">
          <span className="smart-badge sm">A</span>
          <span className="smart-section__title">{t("smart_badge_achievable")}</span>
          <span className="smart-section__hint">{t("smart_section_a_hint")}</span>
        </div>

        <div className="smart-row-2">
          <div className="smart-field">
            <Form.Field error={formik.touched.investment && !!formik.errors.investment}>
              <label>{t("smart_label_investment")} <span className="req">*</span></label>
              <Form.Input
                name="investment" placeholder={t("smart_placeholder_investment")}
                value={formik.values.investment ?? ""}
                onChange={formik.handleChange} onBlur={formik.handleBlur}
              />
              <span className="smart-field__hint">
                {t("smart_hint_investment")}
              </span>
              {formik.touched.investment && formik.errors.investment && (
                <Label basic color="red" pointing>{formik.errors.investment}</Label>
              )}
            </Form.Field>
          </div>

          <div className="smart-field">
            <Form.Field error={formik.touched.responsible && !!formik.errors.responsible}>
              <label>{t("responsible")} <span className="req">*</span></label>
              <Form.Input
                name="responsible" placeholder={t("smart_placeholder_responsible")}
                value={formik.values.responsible ?? ""}
                onChange={formik.handleChange} onBlur={formik.handleBlur}
              />
              <span className="smart-field__hint">
                {t("smart_hint_responsible")}
              </span>
              {formik.touched.responsible && formik.errors.responsible && (
                <Label basic color="red" pointing>{formik.errors.responsible}</Label>
              )}
            </Form.Field>
          </div>
        </div>
      </div>

      {/* ── R — RELEVANTE ── */}
      <div className="smart-section">
        <div className="smart-section__head">
          <span className="smart-badge sm">R</span>
          <span className="smart-section__title">{t("smart_badge_relevant")}</span>
          <span className="smart-section__hint">{t("smart_section_r_hint")}</span>
        </div>

        <div className={`smart-relevance${formik.values.relevantConfirmed ? " is-confirmed" : ""}`}>
          <div className="smart-relevance__content">
            <div className="smart-relevance__icon">
              {formik.values.relevantConfirmed ? "✓" : "+"}
            </div>
            <div className="smart-relevance__text">
              {showChips ? (
                <>
                  {renderTemplate(t("smart_relevance_filled_template"), {
                    action: <strong>{actionText?.toLowerCase()}</strong>,
                    aspectSource: <strong>{aspectText} · {formik.values.source}</strong>,
                  })}
                  <div className="smart-relevance__chips">
                    {(frameworksByAspect[formik.values.aspect] || []).map((fw) => (
                      <span className="smart-tag" key={fw}>{fw}</span>
                    ))}
                  </div>
                </>
              ) : (
                renderTemplate(t("smart_relevance_empty_template"), {
                  aspect: <strong>{t("aspect")}</strong>,
                  action: <strong>{t("action")}</strong>,
                  source: <strong>{t("source")}</strong>,
                })
              )}
            </div>
          </div>

          <div
            className="smart-relevance__confirm"
            role="checkbox"
            tabIndex={0}
            aria-checked={formik.values.relevantConfirmed}
            onClick={() => formik.setFieldValue("relevantConfirmed", !formik.values.relevantConfirmed)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                formik.setFieldValue("relevantConfirmed", !formik.values.relevantConfirmed);
              }
            }}
          >
            <div className={`smart-check${formik.values.relevantConfirmed ? " is-checked" : ""}`}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2.5 6l2.5 2.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="smart-confirm-label">
              {t("smart_relevance_confirm_label")}
            </span>
          </div>
          {formik.submitCount > 0 && formik.errors.relevantConfirmed && (
            <Label basic color="red" pointing="above">{formik.errors.relevantConfirmed}</Label>
          )}
        </div>
      </div>

      {/* ── T — TEMPORAL ── */}
      <div className="smart-section">
        <div className="smart-section__head">
          <span className="smart-badge sm">T</span>
          <span className="smart-section__title">{t("smart_badge_timely")}</span>
          <span className="smart-section__hint">{t("smart_section_t_hint")}</span>
        </div>

        <div className="smart-row-4">
          <div className="smart-field">
            <Form.Field error={formik.touched.year && !!formik.errors.year}>
              <label>{t("smart_label_year")} <span className="req">*</span></label>
              <Form.Select
                options={yearsOptions}
                placeholder={t("select_year")}
                value={formik.values.year ?? null}
                onChange={(_, { value }) => formik.setFieldValue("year", value)}
                onBlur={() => formik.setFieldTouched("year", true)}
              />
              <span className="smart-field__hint">{t("smart_hint_year")}</span>
              {formik.touched.year && formik.errors.year && (
                <Label basic color="red" pointing>{formik.errors.year}</Label>
              )}
            </Form.Field>
          </div>

          <div className="smart-field">
            <Form.Field error={formik.touched.periodStart && !!formik.errors.periodStart}>
              <label>{t("period_start")} <span className="req">*</span></label>
              <Form.Input
                type="date" name="periodStart"
                value={formik.values.periodStart ?? ""}
                onChange={formik.handleChange} onBlur={formik.handleBlur}
              />
              <span className="smart-field__hint">{t("smart_hint_period_start")}</span>
              {formik.touched.periodStart && formik.errors.periodStart && (
                <Label basic color="red" pointing>{formik.errors.periodStart}</Label>
              )}
            </Form.Field>
          </div>

          <div className="smart-field">
            <Form.Field error={formik.touched.periodEnd && !!formik.errors.periodEnd}>
              <label>{t("period_end")} <span className="req">*</span></label>
              <Form.Input
                type="date" name="periodEnd"
                value={formik.values.periodEnd ?? ""}
                onChange={formik.handleChange} onBlur={formik.handleBlur}
              />
              <span className="smart-field__hint">{t("smart_hint_period_end")}</span>
              {formik.touched.periodEnd && formik.errors.periodEnd && (
                <Label basic color="red" pointing>{formik.errors.periodEnd}</Label>
              )}
            </Form.Field>
          </div>

          <div className="smart-field">
            <Form.Field error={formik.touched.frequency && !!formik.errors.frequency}>
              <label>{t("smart_label_frequency")} <span className="req">*</span></label>
              <Form.Select
                options={frequencyOptions}
                value={formik.values.frequency ?? "Trimestral"}
                onChange={(_, { value }) => formik.setFieldValue("frequency", value)}
                onBlur={() => formik.setFieldTouched("frequency", true)}
              />
              <span className="smart-field__hint">{t("smart_hint_frequency")}</span>
              {formik.touched.frequency && formik.errors.frequency && (
                <Label basic color="red" pointing>{formik.errors.frequency}</Label>
              )}
            </Form.Field>
          </div>
        </div>
      </div>

      {/* ── Vista previa ── */}
      <div className="smart-preview">
        <div className="smart-preview__title">{t("smart_preview_title")}</div>
        <p className="smart-preview__text">
          {renderTemplate(t("smart_preview_template"), {
            action: <Token value={previewAction} fallback={t("smart_preview_fallback_action")} />,
            source: <Token value={v.source} fallback={t("smart_preview_fallback_source")} />,
            pct: <Token value={v.reductionTons} fallback={t("smart_preview_fallback_pct")} />,
            year: <Token value={previewYear} fallback={t("smart_preview_fallback_year")} />,
            measure: <Token value={v.measure} fallback={t("smart_preview_fallback_measure")} />,
            investment: <Token value={v.investment} fallback={t("smart_preview_fallback_investment")} />,
            periodStart: <Token value={formatMonthYearAbbrev(v.periodStart)} fallback={t("smart_preview_fallback_period_start")} />,
            periodEnd: <Token value={formatMonthYearAbbrev(v.periodEnd)} fallback={t("smart_preview_fallback_period_end")} />,
          })}
        </p>
      </div>

      {/* ── Footer ── */}
      <div className="smart-form__actions-row">
        <p className="smart-form__footer-note">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="6" stroke="#6B7A8E" strokeWidth="1.3" />
            <path d="M7 4v3.5M7 10h.01" stroke="#6B7A8E" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          {renderTemplate(t("smart_footer_note_template"), { asterisk: <span className="req">*</span> })}
        </p>
        <div className="smart-form__actions">
          <Button type="submit" primary loading={formik.isSubmitting} disabled={formik.isSubmitting}>
            {!objetivesForm ? t("save") : t("update")}
          </Button>
          <Button type="button" onClick={() => (onClose ? onClose() : goBack())}>
            {t("cancel")}
          </Button>
        </div>
      </div>

    </Form>
  );
}
